/**
 * What eager code reads of the FTUX (Home's Spotlight, MainLayout, the QA strip, the pages). Every reader requires
 * the layer to be mounted, so turning the `ftux` flag off mid-run leaves no pick card and no paused nudges behind.
 */
import { createSelector } from '@reduxjs/toolkit';

import { useAppSelector, type RootState } from '@/store';

import type { BrandPieceId, FtuxPieceStep, SkipReason } from '../catalog';
import type { FtuxTargetId } from '../targets';
import { canRunOf, piecesWaitOf, type FtuxState } from './ftuxSlice';
import { prefsReliableFor } from './persistence';
import type { FtuxGate, FtuxPhase, FtuxProgress, FtuxView, PieceAvailability, PieceMeta } from './types';

export const selectFtux = (state: RootState): FtuxState => state.ftux;

/** The owner's preferences are their own, loaded without an error (a failed GET also reads as loaded, with {}). */
export const selectFtuxPrefsReliable = (state: RootState): boolean =>
  prefsReliableFor(state.userPreferences, state.authentication.user.user_id);

/** Whether the layer renders (FtuxRoot's FtuxLayer): the gate open, or latched while a piece plays (canRunOf). */
export const selectFtuxCanRun = (state: RootState): boolean => canRunOf(state.ftux);

/**
 * The FTUX holds the screen: Lumio's hello and the map (on Home, the only page FtuxLayer shows them on), a piece's
 * pop-up, a piece in play or its acknowledgment (MainLayout pauses Lumio's nudges meanwhile). `onHome`: the route is
 * Home. A run at the hello or the map holds nothing on another page, so the nudges carry on there.
 */
export const selectFtuxBusy = (state: RootState, onHome: boolean): boolean => {
  const { ftux } = state;
  if (!canRunOf(ftux)) return false;
  const status = ftux.progress?.status;
  return (onHome && (status === 'hello' || status === 'lap')) || ftux.phase !== 'none';
};

/** The menu map holds Home (after Lumio's hello, or a tester's jump to it). */
export const selectFtuxMapOn = (state: RootState, onHome: boolean): boolean =>
  onHome && canRunOf(state.ftux) && state.ftux.progress?.status === 'lap';

/** The waiting piece's card once it can be offered (ready) and the brand is fully deployed (piecesWaitOf); else null. */
const readyPickOf = (ftux: FtuxState): PieceMeta | null => {
  if (!canRunOf(ftux) || piecesWaitOf(ftux.gate) || ftux.progress?.status !== 'waiting') return null;
  const { piece } = ftux.progress;
  if (piece === null || ftux.availability[piece] !== 'ready') return null;
  return ftux.shipped?.find((meta) => meta.id === piece) ?? null;
};

/**
 * The waiting piece's card while nothing is open (its light laps it, its pop-up's picture warms up): its meta once it
 * can be offered and the brand is fully deployed; else null.
 */
export const selectFtuxWaitingPick = (state: RootState): PieceMeta | null =>
  state.ftux.phase === 'none' ? readyPickOf(state.ftux) : null;

/**
 * The card Home's Spotlight shows: the waiting pick, kept in its place while its own pop-up is open over it (the user,
 * 2026-10-20: Spotlight never hides as the card's button is pressed). A piece in play or its acknowledgment takes it
 * away, the owner off on the piece's page.
 */
export const selectFtuxSpotlightPick = (state: RootState): PieceMeta | null => {
  const { ftux } = state;
  const ownIntro = ftux.phase === 'intro' && ftux.active !== null && ftux.active === ftux.progress?.piece;
  return ftux.phase === 'none' || ownIntro ? readyPickOf(ftux) : null;
};

/** What the QA strip shows its steps and label from (qa/qaSteps.ts). */
export type FtuxQaSource = {
  /** The layer renders (canRunOf); when false, gate.reason says why. gate.deploy says why the pieces wait. */
  canRun: boolean;
  gate: FtuxGate | null;
  hydrated: boolean;
  /** The owner is past Lumio's hello and the map (once per owner, whatever the brand). */
  introDone: boolean;
  progress: FtuxProgress | null;
  phase: FtuxPhase;
  active: BrandPieceId | null;
  view: FtuxView | null;
  /** The shipped pieces in the script's order (empty until published). */
  shipped: PieceMeta[];
  availability: Partial<Record<BrandPieceId, PieceAvailability>>;
  skips: Partial<Record<BrandPieceId, SkipReason>>;
  /** The last write of the owner's records failed (cleared by the next one that lands). */
  saveError: string | null;
  /** The last thing of the studio a step looked for and did not find in this run (its `data-ftux` target). */
  missed: FtuxTargetId | null;
};

/** The strip's source while the layer is mounted, else null. Memoized on the slice. */
export const selectFtuxQa = createSelector([selectFtux], (ftux): FtuxQaSource | null =>
  ftux.mounted
    ? {
        canRun: canRunOf(ftux),
        gate: ftux.gate,
        hydrated: ftux.hydrated,
        introDone: ftux.intro?.status === 'done',
        progress: ftux.progress,
        phase: ftux.phase,
        active: ftux.active,
        view: ftux.view,
        shipped: ftux.shipped ?? [],
        availability: ftux.availability,
        skips: ftux.skips,
        saveError: ftux.save.error,
        missed: ftux.missed,
      }
    : null,
);

/**
 * Whether the piece in play shows the view `key` now: the one page look a step needs (Art Style's New style held
 * hovered). False outside an FTUX step, so the page behaves exactly as before.
 */
export const useFtuxViewIs = (key: FtuxPieceStep): boolean =>
  useAppSelector((state) => state.ftux.mounted && state.ftux.phase === 'playing' && state.ftux.view?.key === key);
